import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { TnplAcceptableRiskLevel } from "@redotech/redo-tnpl-settings-common/tnpl-settings";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { PageForm, PageFormSave } from "@redotech/redo-web/page-form";
import { groupInput, InputSchema } from "@redotech/ui/form";
import classNames from "classnames";
import { memo, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { RedoAdminRpcClientContext } from "../../app/redo-admin-rpc-client-provider";
import { LoadTeamContext, TeamContext } from "../team";
import * as teamCss from "../team.module.css";
import {
  TnplGeneralCard,
  tnplGeneralDefault,
  tnplGeneralForm,
} from "./tnpl/general";

export const TnplPage = memo(function TnplPage() {
  const params = useParams();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const teamId = params.teamId!;

  const team = useContext(TeamContext);
  const loadTeam = useRequiredContext(LoadTeamContext);

  const handleSave = useHandler(() => {
    loadTeam();
  });

  const value: TnplValue | undefined = useMemo(() => {
    if (!team) {
      return;
    }

    return {
      general: {
        enabled: !!team.settings.tnpl?.enabled,
        useMinutesForTrialPeriod:
          !!team.settings.tnpl?.useMinutesForTrialPeriod,
        acceptableRiskLevel:
          team.settings.tnpl?.acceptableRiskLevel ||
          TnplAcceptableRiskLevel.LOW,
      },
    };
  }, [team]);

  return value ? (
    <TnplForm id={teamId} onSave={handleSave} value={value} />
  ) : null;
});

const tnplForm = groupInput({ general: tnplGeneralForm });

type TnplValue = InputSchema.Value<typeof tnplForm>;

const tnplDefault: TnplValue = { general: tnplGeneralDefault };

const TnplForm = memo(function TnplForm({
  id,
  onSave,
  value,
}: {
  id: string;
  onSave: (value: TnplValue) => void;
  value: TnplValue | undefined;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

  const input = useInput(tnplForm, value || tnplDefault);

  const save: PageFormSave = useHandler(async (signal) => {
    const value = input.value;

    // Use the new RPC endpoint to update TNPL settings
    await rpcClient.updateTnplSettings({
      teamId: id,
      enabled: value.general.enabled,
      useMinutesForTrialPeriod: value.general.useMinutesForTrialPeriod,
    });

    onSave(value);
  });

  const { general } = input.inputs;

  return (
    <div className={teamCss.container}>
      <PageForm initial={value || tnplDefault} input={input} save={save}>
        <div className={gridCss.grid}>
          <div className={classNames(gridCss.span12, gridCss.span6L)}>
            <div className={gridCss.grid}>
              <div className={gridCss.span12}>
                <TnplGeneralCard input={general} />
              </div>
            </div>
          </div>
        </div>
      </PageForm>
    </div>
  );
});
